import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Briefcase, MapPin, Mail, Phone, Clock, Upload, FileText, X, CheckCircle2,
  Shield, Users, TrendingUp, Star, Quote, Loader2, RefreshCw, Sparkles, Award,
  HeartHandshake, GraduationCap, Globe, Calendar
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useToast } from '../hooks/use-toast';
import SourceSection from '../components/SourceSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALLOWED_EXT = ['.pdf', '.doc', '.docx'];
const MAX_BYTES = 10 * 1024 * 1024;

const CAREER_TESTIMONIALS = [
  { name: 'Samantha R.', role: 'Logistics Coordinator', quote: 'Excelence treated me like a pro from day one. Clear expectations, real support from dispatch, and a team that genuinely has your back.' },
  { name: 'Marco L.', role: 'Senior Dispatcher', quote: 'I started on the waitlist, interviewed within two weeks, and never looked back. The culture here is built on trust and execution.' },
  { name: 'Nadia P.', role: 'Freight Coordinator', quote: 'Best decision I\u2019ve made. Excelence invests in its people — training, tech, and growth paths are all taken seriously.' },
];

const PERKS = [
  { icon: TrendingUp, title: 'Growth pathways', copy: 'Clear promotion tracks from coordinator to operations leadership.' },
  { icon: Shield, title: 'Stability', copy: 'Decade of on-the-road experience powering a fast-growing NY HQ.' },
  { icon: HeartHandshake, title: 'Team culture', copy: 'Collaborative dispatch floor where every win is a shared win.' },
  { icon: Award, title: 'Recognition', copy: 'Performance bonuses, quarterly awards, and real-time feedback.' },
];

const SHIFT_OPTIONS = ['Day shift', 'Night shift', 'Swing shift', 'Weekends', 'Flexible / Any'];

const formatBytes = (b) => b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

const SectionHead = ({ num, title, subtitle }) => (
  <div className="flex items-start gap-3 mb-4">
    <div className="w-8 h-8 rounded-lg bg-[#0A1628] text-[#2AA8E8] font-extrabold flex items-center justify-center text-sm shrink-0">{num}</div>
    <div>
      <h3 className="text-lg font-extrabold navy-text">{title}</h3>
      {subtitle && <p className="text-sm text-[#4A5A72]">{subtitle}</p>}
    </div>
  </div>
);

const CareersPage = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: '', lastName: '', phone: '', email: '', location: '',
    hasExperience: '', // '' | 'yes' | 'no'
    // Experienced track
    yearsExperience: '', transportationBefore: 'yes', preferredShift: '', startDate: '',
    // Training track
    whyInterested: '', fullTime: 'yes', languages: '', earliestStartDate: '',
    // Source tracking
    source: '', referralName: '', referralPhone: '', otherSource: '',
  });
  const [files, setFiles] = useState([]);
  const [captcha, setCaptcha] = useState(null);
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [hp, setHp] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [counter, setCounter] = useState(null);

  const track = form.hasExperience === 'no' ? 'training_waitlist' : 'experienced';
  const isTraining = form.hasExperience === 'no';

  useEffect(() => {
    document.title = 'Logistics Coordinator Jobs | Excelence Inc';
    const metaDesc = document.querySelector('meta[name="description"]');
    const content = 'Apply to become a logistics coordinator with Excelence Inc. Experienced applicants and future trainees welcome.';
    if (metaDesc) metaDesc.setAttribute('content', content);
    else {
      const m = document.createElement('meta');
      m.name = 'description'; m.content = content; document.head.appendChild(m);
    }
    refreshCaptcha();
    loadCounter();
  }, []);

  const refreshCaptcha = async () => {
    try {
      const { data } = await axios.get(`${API}/waitlist/captcha`);
      setCaptcha(data); setCaptchaAnswer('');
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('refreshCaptcha failed', err);
    }
  };
  const loadCounter = async () => {
    try { const { data } = await axios.get(`${API}/waitlist/stats`); setCounter(data.displayCount); }
    catch (err) {
      if (process.env.NODE_ENV === 'development') console.debug('loadCounter failed', err);
    }
  };

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const onPickFiles = (incoming) => {
    const list = Array.from(incoming);
    const merged = [...files];
    for (const f of list) {
      if (merged.length >= 2) { toast({ title: 'Maximum 2 files' }); break; }
      const ext = '.' + (f.name.split('.').pop() || '').toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { toast({ title: 'File type not allowed', description: 'PDF, DOC, DOCX only.' }); continue; }
      if (f.size > MAX_BYTES) { toast({ title: 'File too large', description: `${f.name} exceeds 10MB.` }); continue; }
      merged.push(f);
    }
    setFiles(merged.slice(0, 2));
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const scrollToForm = () => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' });

  const submit = async (e) => {
    e.preventDefault();
    // Shared required
    if (!form.firstName || !form.lastName || !form.phone || !form.email || !form.location) {
      return toast({ title: 'Please complete your personal information' });
    }
    if (!form.hasExperience) {
      return toast({ title: 'Please tell us about your experience', description: 'Select Yes or No.' });
    }
    // Track-specific
    if (!isTraining) {
      if (!form.yearsExperience) return toast({ title: 'Please enter your years of experience' });
      if (!form.startDate) return toast({ title: 'Please provide your available start date' });
    } else {
      if (!form.whyInterested) return toast({ title: 'Please tell us why you want to join' });
      if (!form.earliestStartDate) return toast({ title: 'Please enter your earliest start date' });
    }
    if (!captcha || captchaAnswer === '') return toast({ title: 'Please answer the security question' });
    // Source required + conditional
    if (!form.source) return toast({ title: 'Please tell us where you heard about us' });
    if (form.source === 'Friend / Referral' && (!form.referralName || !form.referralPhone)) {
      return toast({ title: 'Please provide your friend\u2019s name and phone' });
    }
    if ((form.source === 'Existing Driver' || form.source === 'Existing Employee') && !form.referralName) {
      return toast({ title: `Please provide the ${form.source === 'Existing Driver' ? 'driver' : 'employee'} name` });
    }
    if (form.source === 'Other' && !form.otherSource) {
      return toast({ title: 'Please specify your source' });
    }

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('firstName', form.firstName);
      fd.append('lastName', form.lastName);
      fd.append('phone', form.phone);
      fd.append('email', form.email);
      fd.append('location', form.location);
      fd.append('track', track);
      if (!isTraining) {
        fd.append('yearsExperience', form.yearsExperience);
        fd.append('transportationBefore', form.transportationBefore === 'yes');
        if (form.preferredShift) fd.append('preferredShift', form.preferredShift);
        fd.append('startDate', form.startDate);
      } else {
        fd.append('whyInterested', form.whyInterested);
        fd.append('fullTime', form.fullTime === 'yes');
        if (form.languages) fd.append('languages', form.languages);
        fd.append('earliestStartDate', form.earliestStartDate);
      }
      fd.append('hp', hp);
      fd.append('captchaA', captcha.a);
      fd.append('captchaB', captcha.b);
      fd.append('captchaAnswer', captchaAnswer);
      fd.append('captchaToken', captcha.token);
      // Source
      fd.append('source', form.source);
      if (form.referralName) fd.append('referralName', form.referralName);
      if (form.referralPhone) fd.append('referralPhone', form.referralPhone);
      if (form.otherSource) fd.append('otherSource', form.otherSource);
      files.forEach(f => fd.append('files', f));

      await axios.post(`${API}/waitlist`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
      loadCounter();
    } catch (err) {
      console.error(err);
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please check fields and try again.' });
      refreshCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const successMessage = isTraining
    ? 'Thank you for joining our training waitlist. We will contact you when future training opportunities open.'
    : 'Thank you for applying. Our hiring team will review your profile and contact you soon.';

  const successTitle = isTraining ? 'Welcome to the training waitlist' : 'Application received';

  return (
    <>
      <Header />
      {!done && (
        <button onClick={scrollToForm} className="fixed bottom-6 right-6 z-40 btn-accent font-bold px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 md:hidden">
          <Briefcase className="w-4 h-4" /> Apply Now
        </button>
      )}

      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0A1628] text-white">
          <div className="absolute inset-0 z-0 brand-gradient-tech" />
          <div className="absolute inset-0 z-0 circuit-bg-dark opacity-50" />
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2AA8E8]/20 blur-3xl z-0" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-[#D4A24C]/15 blur-3xl z-0" />

          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2AA8E8]/10 border border-[#2AA8E8]/30 text-xs font-bold text-[#2AA8E8] mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Now accepting applications
            </div>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] max-w-4xl">
              <span className="text-[#2AA8E8]">Logistics</span> <span className="gold-text">Coordinators</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg md:text-xl text-white/80">
              Join Excelence Inc and grow with a professional transportation team. We are looking for motivated people ready to build a future in logistics.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button onClick={scrollToForm} className="btn-accent h-12 px-8 font-bold">Become a Logistics Coordinator With Us</Button>
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10">
                <Users className="w-4 h-4 text-[#2AA8E8]" />
                <span className="text-sm font-semibold"><strong className="text-[#D4A24C]">{counter ?? '—'}+</strong> people joined</span>
              </div>
            </div>

            {/* Quick trust row */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Shield, label: 'Fully insured', sub: 'MC1766140 · DOT4474028' },
                { icon: Users, label: '392+ partners', sub: 'Brokerage network' },
                { icon: TrendingUp, label: '10+ years', sub: 'Combined experience' },
                { icon: Star, label: '4.9/5', sub: 'Team rating' },
              ].map(({ icon: Icon, label, sub }, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 backdrop-blur-sm">
                  <Icon className="w-5 h-5 text-[#2AA8E8] mb-2" />
                  <div className="font-bold text-sm">{label}</div>
                  <div className="text-xs text-white/60">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Perks */}
        <section className="section bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.25em] gold-text">Why Excelence</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold navy-text leading-tight">A premium workplace for serious logistics professionals.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {PERKS.map((p, i) => (
                <div key={i} className="card-lift bg-white rounded-2xl p-6 border border-[#E5EAF2]">
                  <div className="w-12 h-12 rounded-xl bg-[#0A1628] flex items-center justify-center mb-4">
                    <p.icon className="w-6 h-6 text-[#2AA8E8]" />
                  </div>
                  <h3 className="text-lg font-extrabold navy-text mb-2">{p.title}</h3>
                  <p className="text-sm text-[#4A5A72]">{p.copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form */}
        <section id="apply-form" className="section bg-[#F7F9FC]">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            {done ? (
              <div className="bg-white rounded-3xl shadow-xl p-10 md:p-14 border border-[#E5EAF2] text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-3xl md:text-4xl font-extrabold navy-text mb-3">{successTitle}</h2>
                <p className="text-[#4A5A72] text-lg max-w-xl mx-auto">{successMessage}</p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <a href="/"><Button variant="outline" className="font-semibold">Back to home</Button></a>
                  <a href="/drivers"><Button className="btn-accent font-bold">Driver roles</Button></a>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl p-6 md:p-10 border border-[#E5EAF2]">
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-11 h-11 rounded-xl bg-[#0A1628] flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-[#2AA8E8]" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-extrabold navy-text">Become a Logistics Coordinator With Us</h2>
                    <p className="text-sm text-[#4A5A72]">Fields marked with <span className="text-[#D4A24C]">*</span> are required.</p>
                  </div>
                </div>

                {/* Honeypot */}
                <div className="hidden" aria-hidden>
                  <input tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
                </div>

                {/* 1. Personal */}
                <SectionHead num="1" title="Personal information" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div>
                    <Label>First name <span className="text-[#D4A24C]">*</span></Label>
                    <Input className="mt-1.5 h-11" value={form.firstName} onChange={e => update({ firstName: e.target.value })} placeholder="Jane" required />
                  </div>
                  <div>
                    <Label>Last name <span className="text-[#D4A24C]">*</span></Label>
                    <Input className="mt-1.5 h-11" value={form.lastName} onChange={e => update({ lastName: e.target.value })} placeholder="Doe" required />
                  </div>
                  <div>
                    <Label>Phone <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input type="tel" className="pl-9 h-11" value={form.phone} onChange={e => update({ phone: e.target.value })} placeholder="(555) 000-0000" required />
                    </div>
                  </div>
                  <div>
                    <Label>Email <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input type="email" className="pl-9 h-11" value={form.email} onChange={e => update({ email: e.target.value })} placeholder="you@email.com" required />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label>Current city / state <span className="text-[#D4A24C]">*</span></Label>
                    <div className="relative mt-1.5">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                      <Input className="pl-9 h-11" value={form.location} onChange={e => update({ location: e.target.value })} placeholder="Brooklyn, NY" required />
                    </div>
                  </div>
                </div>

                {/* 2. Experience gate */}
                <SectionHead num="2" title="Do you already have training or experience as a Logistics Coordinator?" />
                <RadioGroup value={form.hasExperience} onValueChange={v => update({ hasExperience: v })} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
                  {[
                    { v: 'yes', label: 'Yes — I have experience', icon: Award, sub: 'Apply for open positions' },
                    { v: 'no', label: 'No — but I want to learn', icon: GraduationCap, sub: 'Join the training waitlist' },
                  ].map(o => (
                    <label key={o.v} className={`border rounded-xl px-5 py-5 cursor-pointer flex items-start gap-3 transition-colors ${form.hasExperience === o.v ? 'border-[#2AA8E8] bg-[#EFF8FD]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                      <RadioGroupItem value={o.v} id={`exp-${o.v}`} className="mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <o.icon className={`w-5 h-5 ${form.hasExperience === o.v ? 'text-[#2AA8E8]' : 'text-[#4A5A72]'}`} />
                          <div className="font-bold navy-text">{o.label}</div>
                        </div>
                        <div className="text-xs text-[#4A5A72] mt-1">{o.sub}</div>
                      </div>
                    </label>
                  ))}
                </RadioGroup>

                {/* 3a. Experienced track */}
                {form.hasExperience === 'yes' && (
                  <>
                    <div className="h-px bg-[#E5EAF2] my-6" />
                    <SectionHead num="3" title="Your experience" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <Label>Years of experience <span className="text-[#D4A24C]">*</span></Label>
                        <div className="relative mt-1.5">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                          <Input type="number" min="0" className="pl-9 h-11" value={form.yearsExperience} onChange={e => update({ yearsExperience: e.target.value })} placeholder="e.g. 3" required />
                        </div>
                      </div>
                      <div>
                        <Label>Worked in transportation before?</Label>
                        <RadioGroup value={form.transportationBefore} onValueChange={v => update({ transportationBefore: v })} className="grid grid-cols-2 gap-2 mt-1.5">
                          {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
                            <label key={v} className={`border rounded-lg px-3 py-2.5 cursor-pointer flex items-center gap-2 transition-colors ${form.transportationBefore === v ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                              <RadioGroupItem value={v} id={`tb-${v}`} />
                              <span className="font-semibold text-sm navy-text">{l}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </div>
                      <div>
                        <Label>Preferred shift</Label>
                        <Select value={form.preferredShift} onValueChange={v => update({ preferredShift: v })}>
                          <SelectTrigger className="mt-1.5 h-11"><SelectValue placeholder="Select preferred shift (optional)" /></SelectTrigger>
                          <SelectContent>
                            {SHIFT_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Available start date <span className="text-[#D4A24C]">*</span></Label>
                        <div className="relative mt-1.5">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                          <Input type="date" className="pl-9 h-11" value={form.startDate} onChange={e => update({ startDate: e.target.value })} required />
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* 3b. Training waitlist */}
                {form.hasExperience === 'no' && (
                  <>
                    <div className="h-px bg-[#E5EAF2] my-6" />
                    <div className="bg-gradient-to-br from-[#EFF8FD] to-[#FFF8EC] border border-[#2AA8E8]/20 rounded-2xl p-6 mb-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-[#0A1628] flex items-center justify-center">
                          <GraduationCap className="w-5 h-5 text-[#2AA8E8]" />
                        </div>
                        <div>
                          <h3 className="font-extrabold navy-text text-lg">Future Logistics Coordinator Training Waitlist</h3>
                          <p className="text-sm text-[#4A5A72]">No experience? No problem. Join our waitlist for future training opportunities with Excelence Inc.</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 mb-6">
                      <div>
                        <Label>Why do you want to become a logistics coordinator? <span className="text-[#D4A24C]">*</span></Label>
                        <Textarea rows={4} className="mt-1.5" value={form.whyInterested} onChange={e => update({ whyInterested: e.target.value })} placeholder="Share your motivation and what excites you about logistics…" required />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label>Are you available full time?</Label>
                          <RadioGroup value={form.fullTime} onValueChange={v => update({ fullTime: v })} className="grid grid-cols-2 gap-2 mt-1.5">
                            {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
                              <label key={v} className={`border rounded-lg px-3 py-2.5 cursor-pointer flex items-center gap-2 transition-colors ${form.fullTime === v ? 'border-[#D4A24C] bg-[#FFF8EC]' : 'border-[#E5EAF2] hover:border-[#0A1628]/30'}`}>
                                <RadioGroupItem value={v} id={`ft-${v}`} />
                                <span className="font-semibold text-sm navy-text">{l}</span>
                              </label>
                            ))}
                          </RadioGroup>
                        </div>
                        <div>
                          <Label>Languages you speak</Label>
                          <div className="relative mt-1.5">
                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                            <Input className="pl-9 h-11" value={form.languages} onChange={e => update({ languages: e.target.value })} placeholder="e.g. English, Spanish" />
                          </div>
                        </div>
                        <div>
                          <Label>Earliest start date <span className="text-[#D4A24C]">*</span></Label>
                          <div className="relative mt-1.5">
                            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A5A72]" />
                            <Input type="date" className="pl-9 h-11" value={form.earliestStartDate} onChange={e => update({ earliestStartDate: e.target.value })} required />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* 4. Source (shown once a track is picked) */}
                {form.hasExperience && (
                  <>
                    <div className="h-px bg-[#E5EAF2] my-6" />
                    <SourceSection
                      value={form.source}
                      referralName={form.referralName}
                      referralPhone={form.referralPhone}
                      otherSource={form.otherSource}
                      onChange={update}
                      SectionHead={SectionHead}
                      num="4"
                    />
                  </>
                )}

                {/* 5. Attachments (shown once a track is picked) */}
                {form.hasExperience && (
                  <>
                    <SectionHead num="5" title="Attachments" subtitle={isTraining ? 'Optional — attach a resume or certificate if you have one.' : 'Recommended — attach your resume or cover letter. Up to 2 files.'} />
                    <label
                      htmlFor="coord-file-input"
                      className="block cursor-pointer rounded-2xl border-2 border-dashed border-[#E5EAF2] hover:border-[#2AA8E8] hover:bg-[#F7F9FC] transition-colors p-8 text-center"
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); onPickFiles(e.dataTransfer.files); }}
                    >
                      <Upload className="w-8 h-8 text-[#2AA8E8] mx-auto mb-3" />
                      <div className="font-bold navy-text">Click to upload or drag & drop</div>
                      <div className="text-xs text-[#4A5A72] mt-1">PDF, DOC, DOCX · up to 2 files · 10MB each</div>
                      <input id="coord-file-input" type="file" className="hidden" multiple accept=".pdf,.doc,.docx" onChange={e => onPickFiles(e.target.files)} />
                    </label>
                    {files.length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {files.map((f, i) => (
                          <li key={i} className="flex items-center gap-3 bg-[#F7F9FC] border border-[#E5EAF2] rounded-lg px-4 py-3">
                            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-[#E5EAF2]"><FileText className="w-5 h-5 text-[#2AA8E8]" /></div>
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-sm navy-text truncate">{f.name}</div>
                              <div className="text-xs text-[#4A5A72]">{formatBytes(f.size)}</div>
                            </div>
                            <button type="button" onClick={() => removeFile(i)} className="w-8 h-8 rounded-lg bg-white border border-[#E5EAF2] hover:border-red-300 hover:text-red-600 flex items-center justify-center"><X className="w-4 h-4" /></button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* Captcha */}
                    <div className="mt-8 bg-[#F7F9FC] rounded-xl p-4 border border-[#E5EAF2] flex items-center gap-4 flex-wrap">
                      <Shield className="w-5 h-5 text-[#2AA8E8] shrink-0" />
                      <div className="flex-1 min-w-[160px]">
                        <Label className="text-xs font-bold uppercase tracking-wider text-[#4A5A72]">Security check</Label>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="font-bold navy-text">What is {captcha ? captcha.question : '...'} ?</span>
                          <Input type="number" value={captchaAnswer} onChange={e => setCaptchaAnswer(e.target.value)} className="w-24 h-10" placeholder="?" />
                          <button type="button" onClick={refreshCaptcha} className="text-[#4A5A72] hover:text-[#0A1628]" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
                        </div>
                      </div>
                    </div>

                    <Button type="submit" disabled={loading} className={`mt-8 w-full h-12 text-base font-bold gap-2 ${isTraining ? 'btn-accent' : 'btn-primary'}`}>
                      {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : (
                        isTraining ? <>Join Training Waitlist <GraduationCap className="w-4 h-4" /></> : <>Apply Now <Briefcase className="w-4 h-4" /></>
                      )}
                    </Button>
                    <p className="text-xs text-[#4A5A72] mt-4 text-center">By submitting, you agree to be contacted about opportunities at Excelence Inc.</p>
                  </>
                )}
              </form>
            )}
          </div>
        </section>

        {/* Testimonials */}
        <section className="section bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="max-w-2xl mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.25em] gold-text">Team stories</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-extrabold navy-text leading-tight">What our people say.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {CAREER_TESTIMONIALS.map((t, i) => (
                <article key={i} className="card-lift bg-white rounded-2xl p-6 border border-[#E5EAF2]">
                  <Quote className="w-7 h-7 text-[#D4A24C] mb-3" />
                  <div className="flex items-center gap-0.5 mb-3">
                    {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[#D4A24C] text-[#D4A24C]" />)}
                  </div>
                  <p className="text-[#0A1628] mb-4">“{t.quote}”</p>
                  <div className="border-t border-[#F0F3F8] pt-3">
                    <div className="font-extrabold navy-text">{t.name}</div>
                    <div className="text-xs text-[#4A5A72]">{t.role}</div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-[#F7F9FC] py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="bg-[#0A1628] rounded-3xl p-8 md:p-14 text-white text-center relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#2AA8E8]/15 blur-2xl" />
              <div className="relative">
                <h3 className="text-3xl md:text-4xl font-extrabold leading-tight">Ready to build your career with us?</h3>
                <p className="mt-3 text-white/70 max-w-xl mx-auto">Join {counter ?? 'our'} logistics professionals already on the waitlist.</p>
                <Button onClick={scrollToForm} className="btn-accent mt-6 h-12 px-8 font-bold">Become a Logistics Coordinator With Us</Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default CareersPage;
